import { getEncoding } from "js-tiktoken";

import { ai as aiApi } from "../../../apis-v2/callables";
import { IdempotentRequestAlreadyHandledError } from "../../../apis-v2/errors";
import * as ApiErrors from "../../../apis-v2/errors";
import {
    ClassifyPresentationPromptRequest,
    DEFAULT_AI_IMAGEMODEL,
    FinalizeSlidePrompt,
    GenerateChatResponseMessageRequest,
    GenerateTeamAssetMetadataBackfillRequest,
    GenerateTeamAssetMetadataRequest,
    GenerateImagesRequest,
    GenerateImagesResponse,
    GenerateOutlineQuestionsRequest,
    RebuildTemplateSlidePrompt,
    GenerateSlideDataRequest,
    GenerateSlideNotesRequest,
    GenerateWordCloudRequest,
    GenerationFile,
    legacy_GeneratePresentationOutlineRequest,
    legacy_GenerateSlideRequest,
    MAX_CONTEXT_FILE_SIZE,
    MAX_CONTEXT_TOKEN_COUNT,
    PlanTemplatePresentationPrompt,
    RewriteTextRequest,
    TranslatePresentationRequest,
    TranslateSlideRequest,
    TranslateTextRequest
} from "../../../common/aiConstants";
import { app } from "../../namespaces";
import Api from "../api";
import { uploadBlobToSignedUrl } from "../utilities/uploadBlobToSignedUrl";

export interface legacy_GeneratePresentationOutlineRequestWithFiles extends legacy_GeneratePresentationOutlineRequest {
    files?: File[];
}

export interface legacy_GenerateSlideRequestWithFiles extends legacy_GenerateSlideRequest {
    files?: File[];
}

export interface CollectionItemModel {
    text: {
        blocks: Record<string, any>[]
    },
    content_type?: string,
    content_value?: string
}

export class ContextFileTooLargeError extends Error { }

/**
 * The extension to ask /tempAssets for, or undefined when the file's name does not carry one we can
 * use. `name.split(".").pop()` is not that: on a name with no dot it returns the whole name, so
 * "Q3 report" produced a temp asset called "<uuid>.Q3 report" that the generation endpoints refuse.
 */
const MAX_UPLOAD_EXTENSION_LENGTH = 10;

export function getUploadExtension(fileName: string): string | undefined {
    const separatorIndex = (fileName ?? "").lastIndexOf(".");
    if (separatorIndex <= 0) {
        return undefined;
    }

    const extension = fileName.slice(separatorIndex + 1);
    if (!extension || extension.length > MAX_UPLOAD_EXTENSION_LENGTH || !/^[A-Za-z0-9]+$/.test(extension)) {
        return undefined;
    }

    return extension;
}

class AiGenerationService {
    private async _uploadFilesToTempAssetStorage(files: File[]): Promise<string[]> {
        return await Promise.all(files.map(async file => {
            const { writeUrl, fileName: rawFileName } = await Api.tempAssets.post({ extension: getUploadExtension(file.name) });
            await uploadBlobToSignedUrl(file, writeUrl);
            return rawFileName;
        }));
    }

    private _validateContextFileSizes(files: File[]) {
        const totalFileSize = files.reduce((size, file) => size + file.size, 0);
        if (totalFileSize > MAX_CONTEXT_FILE_SIZE) {
            let message = files.length === 1 ? `File ${files[0].name} is` : `Files ${files.map(file => file.name).join(", ")} are`;
            message += ` too large, please use files smaller than ${MAX_CONTEXT_FILE_SIZE / 1024 / 1024}MB`;
            throw new ContextFileTooLargeError(message);
        }
    }

    public async getCreditsBalance(workspaceId: string) {
        const { balance } = await aiApi.getCreditsBalance({ workspaceId });
        return balance;
    }

    public async legacy_generatePresentationOutline(
        request: legacy_GeneratePresentationOutlineRequestWithFiles
    ) {
        const { response } = await aiApi.legacy_generatePresentationOutline({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async legacy_generateSlide(
        request: legacy_GenerateSlideRequestWithFiles,
    ) {
        request.files = request.files ?? [];
        request.tempAssetFileNames = request.tempAssetFileNames ?? [];

        if (request.files.length > 0) {
            this._validateContextFileSizes(request.files);
            const fileNames = await this._uploadFilesToTempAssetStorage(request.files);
            request.tempAssetFileNames.push(...fileNames);
        }
        delete request.files;

        const { response } = await aiApi.legacy_generateSlide({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async legacy_generateSlidePublic(
        request: legacy_GenerateSlideRequestWithFiles,
    ) {
        const { response } = await aiApi.legacy_generateSlidePublic({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async finalizeSlide(
        id: string,
        prompt: FinalizeSlidePrompt,
        workspaceId: string
    ) {
        await aiApi.finalizeSlide({
            id,
            prompt,
            workspaceId
        }).catch(err => {
            if (err instanceof IdempotentRequestAlreadyHandledError) {
                return;
            }
            throw err;
        });
    }

    public async rebuildTemplateSlide(
        id: string,
        prompt: RebuildTemplateSlidePrompt,
        workspaceId: string
    ) {
        const { response } = await aiApi.rebuildTemplateSlide({
            id,
            prompt,
            workspaceId
        });

        return response;
    }

    public async planTemplatePresentation(
        id: string,
        prompt: PlanTemplatePresentationPrompt,
        workspaceId: string
    ) {
        const { response } = await aiApi.planTemplatePresentation({
            id,
            prompt,
            workspaceId
        });

        return response;
    }

    public async generateWordCloud(
        request: Omit<GenerateWordCloudRequest, "traceData">
    ) {
        const { response } = await aiApi.generateWordCloud({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async rewriteText(
        request: Omit<RewriteTextRequest, "traceData">
    ) {
        const { response } = await aiApi.rewriteText({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async generateSlideNotes(
        request: GenerateSlideNotesRequest
    ) {
        const { response } = await aiApi.generateSlideNotes({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async analyzeWebpage(url: string) {
        const { response } = await aiApi.analyzeWebpage({
            url,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async generateTeamAssetMetadata(
        request: Omit<GenerateTeamAssetMetadataRequest, "workspaceId">
    ) {
        const { response } = await aiApi.generateTeamAssetMetadata({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public generateTeamAssetMetadataBackfill(
        request: Omit<GenerateTeamAssetMetadataBackfillRequest, "workspaceId">
    ) {
        return aiApi.generateTeamAssetMetadataBackfill({
            ...request,
            workspaceId: app.appController.workspaceId
        });
    }

    public async generateImages(
        request: Omit<GenerateImagesRequest, "traceData">
    ) {
        const { response, remainingCreditsBalance } = await aiApi.generateImages({
            ...request,
            model: request.model ?? DEFAULT_AI_IMAGEMODEL,
            workspaceId: app.appController.workspaceId
        });

        return { response, remainingCreditsBalance } as { response: GenerateImagesResponse, remainingCreditsBalance: number };
    }

    public async translateText(
        request: Omit<TranslateTextRequest, "traceData">
    ) {
        const { response } = await aiApi.translateText({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async translateSlide(
        request: Omit<TranslateSlideRequest, "traceData">
    ) {
        const { response } = await aiApi.translateSlide({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async translatePresentation(
        request: Omit<TranslatePresentationRequest, "traceData">
    ) {
        const { response } = await aiApi.translatePresentation({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async classifyPresentationPrompt(request: ClassifyPresentationPromptRequest) {
        const { response, traceData } = await aiApi.classifyPresentationPrompt({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return { response, traceData };
    }

    public async generateOutlineQuestions(request: GenerateOutlineQuestionsRequest) {
        const { response, traceData } = await aiApi.generateOutlineQuestions({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return { response, traceData };
    }

    public async generateChatResponseMessage(request: GenerateChatResponseMessageRequest) {
        const { response, traceData } = await aiApi.generateChatResponseMessage({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return { response, traceData };
    }

    public async generateSlideData(request: GenerateSlideDataRequest) {
        const { response } = await aiApi.generateSlideData({
            ...request,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async generateSlideImagePrompt(slidePrompt: string, presentationTopic: string, replaceVisualIdea: boolean) {
        const { response } = await aiApi.generateSlideImagePrompt({
            slidePrompt,
            presentationTopic,
            replaceVisualIdea,
            workspaceId: app.appController.workspaceId
        });

        return response;
    }

    public async regenerateSlideImage(request: Omit<Parameters<typeof aiApi.regenerateSlideImage>[0], "workspaceId">) {
        return await aiApi.regenerateSlideImage({
            ...request,
            workspaceId: app.appController.workspaceId
        });
    }

    public async generateImagesForSlide(request: Omit<Parameters<typeof aiApi.generateImagesForSlide>[0], "workspaceId">) {
        return await aiApi.generateImagesForSlide({
            ...request,
            workspaceId: app.appController.workspaceId
        });
    }

    public async saveContextDocuments(files: GenerationFile[]) {
        return await aiApi.saveContextDocuments({
            files,
            workspaceId: app.appController.workspaceId
        });
    }

    public async parseDocument(file: File, currentFiles: GenerationFile[]) {
        this._validateContextFileSizes([file]);

        let { writeUrl, fileName: rawFileName } = await Api.tempAssets.post({});

        let parsedFile: { fileName: string, fileSize: number, cl100kBaseTokenCount: number };
        if (file.type.startsWith("text/") || file.name.endsWith(".txt") || file.name.endsWith(".md")) {
            let text = await new Promise<string>((resolve, reject) => {
                let reader = new FileReader();
                reader.onload = e => resolve(e.target.result as string);
                reader.onerror = reject;
                reader.readAsText(file);
            });

            let htmlFile = new File([text], file.name, { type: "text/html" });
            await uploadBlobToSignedUrl(htmlFile, writeUrl, "text/html");

            let encoding = getEncoding("cl100k_base");
            let cl100kBaseTokenCount = encoding.encode(text).length;

            parsedFile = {
                fileName: rawFileName,
                fileSize: file.size,
                cl100kBaseTokenCount
            };
        } else {
            await uploadBlobToSignedUrl(file, writeUrl, file.type);

            ({ parsedFile } = await aiApi.parseDocument({
                fileName: rawFileName,
                workspaceId: app.appController.workspaceId,
                includeImages: false
            }));
        }

        if (parsedFile.cl100kBaseTokenCount > MAX_CONTEXT_TOKEN_COUNT) {
            throw new ApiErrors.ContentTooLargeError("File is too large, please upload a smaller file");
        }

        let tokenCount = currentFiles.reduce((acc, f) => !f.hasBeenUsed ? acc + f.parsedCl100kBaseTokenCount : acc, 0);
        tokenCount += parsedFile.cl100kBaseTokenCount;
        if (tokenCount > MAX_CONTEXT_TOKEN_COUNT) {
            throw new ApiErrors.ContentTooLargeError("The files you uploaded exceed the maximum allowed files size, please use smaller files");
        }

        const generationFile: GenerationFile = {
            name: file.name,
            type: file.type,
            isProcessing: false,
            parsedFileName: parsedFile.fileName,
            parsedFileSize: parsedFile.fileSize,
            parsedCl100kBaseTokenCount: parsedFile.cl100kBaseTokenCount,
            rawFileName,
            hasBeenUsed: false,
        };

        return generationFile;
    }
}

const aiGenerationService = new AiGenerationService();
export default aiGenerationService;
